import i18n from 'i18n.js';
import { ze, isZh } from 'utils/zhEn';
import { isAndroidOrHarmony, isHarmony, isIos, isMac, isMobile, isPcOrLinux, isWechatMiniProgram, PlatformUtil } from 'utils/platform';
import isString from 'lodash/lang/isString';
import { DocsUtil } from 'utils/docs-util';
import { isNullish } from './nullish';
import { VersionUtil } from 'utils/version-util';
import MD5 from 'md5.js';
import { getBackendInjectEnvs } from 'utils/backend-inject-envs';

export const CrashAttachmentFileType = Object.freeze({
  OTHER_THREAD: 2,
});

const MOBILE_SDK_LOG_FILENAME = 'LogObserver.log.zip';
const PC_SDK_LOG_FILENAME = 'CustomizedLogFile.gz';
const PC_SDK_LOG_FILENAME_NEW_FORMAT = 'CustomizedLogFile.zip';
const WECHAT_MINI_PROGRAM_SDK_LOG_FILENAME = 'CustomizedLogFile.zip';
const SDK_ATTACK_FILENAME = 'CustomizedAttachFile.zip'; // SDK新增的直传cos的其他类附件（和log类附件区分）

const MINIDUMP_STACK_MEMORY_FILENAME = 'MinidumpStackMemory.txt';

const PLAYSTATION_CORE_DUMP_FILE_SET_FILENAME = 'PlayStationCrashResources.zip';

const CUSTOM_LOG_COMMON_FILE_INFO = {
  alias: 'PrintLog.log',
  label: i18n.t('REPORTDETAIL.自定义日志来自接口'),
  dataSourceType: 'API',
  dataSourceDesc: 'PrintLog',
  noteMobileMax: ze('日志大小不超过30KB。','The log size should not exceed 30KB.'),
  notePcMax: ze('单条不超过4KB，总共不超过30KB。','The log size should not exceed 30KB.'),
  link: {
    android: DocsUtil.makeDocsUrl('/sdkDocuments/mobile-sdk/#custom-log'),
    ios: DocsUtil.makeDocsUrl('/sdkDocuments/mobile-sdk/#custom-log-reporting'),
    pc: DocsUtil.makeDocsUrl("/sdkDocuments/pc-sdk/#custom-interface-log"),
    unity: DocsUtil.makeDocsUrl("/sdkDocuments/unity-sdk/#add-custom-log"),
    unreal: DocsUtil.makeDocsUrl("/sdkDocuments/unreal-sdk/#custom-log"),
  },
};

const knownAttachmentFileInfoList = [{
  value: 'extraMessage.txt',
  alias: 'ExtraMessage.txt',
  dataSourceType: 'API',
  dataSourceDesc: 'OnCrashExtraMessageNotify',
  extraInfo:{
    tagMobileMax:'1MB',
    tagPcMax:'1MB',
    noteMobileMax:ze('KV长度之和不超过1MB，KV数不超过500对。','The sum of KV lengths should not exceed 1MB, and the number of KVs should not exceed 500.'),
    notePcMax:ze('KV长度之和不超过1MB，KV数不超过500对。','The sum of KV lengths should not exceed 1MB, and the number of KVs should not exceed 500.'),
  },
  extraMsg:{
    tagMobileMax:'128KB',
    tagPcMax:'128KB',
    noteMobileMax:ze('大小不超过128KB。','The size should not exceed 128KB.'),
    notePcMax:ze('大小不超过128KB。','The size should not exceed 128KB.'),
  },
  link:{
    android: DocsUtil.makeDocsUrl('/sdkDocuments/mobile-sdk/#set-callback'),
    ios: DocsUtil.makeDocsUrl('/sdkDocuments/mobile-sdk/#set-exception-callback'),
    unity: DocsUtil.makeDocsUrl("/sdkDocuments/unity-sdk/#set-callback"),
    unreal: DocsUtil.makeDocsUrl("/sdkDocuments/unreal-sdk/#set-callback"),
  }
}, {
  value: 'extraData', //同userExtraByteData
  alias: 'ExtraData',
  dataSourceType: 'API',
  dataSourceDesc: 'OnCrashExtraDataNotify',
  noteAndroidMax: ze('附加数据长度不超过128KB。','The length of the additional data should not exceed 128KB.'),
  link: {
    android: DocsUtil.makeDocsUrl('/sdkDocuments/mobile-sdk/#set-callback'),
    unity: DocsUtil.makeDocsUrl("/sdkDocuments/unity-sdk/#set-callback"),
    unreal: DocsUtil.makeDocsUrl("/sdkDocuments/unreal-sdk/#set-callback"),
  }
}, {
  value: 'trace.zip',
  alias: 'Trace.zip',
  downloadFilename: 'trace.txt',
  dataSourceDesc: ze('SDK采集的dump文件','The dump file captured by SDK'),
}, {
  value: 'crash_attach.log',//同extraMessage.txt, PC
  alias: 'CrashAttach.log',
  dataSourceType: 'API',
  dataSourceDesc: 'OnCrashExtraMessageNotify',
  extraInfo:{
    tagMobileMax:'1MB',
    tagPcMax:'1MB',
    noteMobileMax:ze('KV长度之和不超过1MB，KV数不超过500对。','The sum of KV lengths should not exceed 1MB, and the number of KVs should not exceed 500.'),
    notePcMax:ze('KV长度之和不超过1MB，KV数不超过500对。','The sum of KV lengths should not exceed 1MB, and the number of KVs should not exceed 500.'),
  },
  extraMsg:{
    tagMobileMax:'128KB',
    tagPcMax:'128KB',
    noteMobileMax:ze('大小不超过128KB。','The size should not exceed 128KB.'),
    notePcMax:ze('大小不超过128KB。','The size should not exceed 128KB.'),
  },
  link:{
    android: DocsUtil.makeDocsUrl('/sdkDocuments/mobile-sdk/#set-callback'),
    ios: DocsUtil.makeDocsUrl('/sdkDocuments/mobile-sdk/#set-exception-callback'),
    unity: DocsUtil.makeDocsUrl("/sdkDocuments/unity-sdk/#set-callback"),
    unreal: DocsUtil.makeDocsUrl("/sdkDocuments/unreal-sdk/#set-callback"),
  }
}, {
  value: 'userExtraByteData',
  dataSourceType: 'API',
  dataSourceDesc: 'OnCrashExtraDataNotify',
  noteAndroidMax: ze('附加数据长度不超过128KB。','The length of the additional data should not exceed 128KB.'),
  link: {
    android: DocsUtil.makeDocsUrl('/sdkDocuments/mobile-sdk/#set-callback'),
    unity: DocsUtil.makeDocsUrl("/sdkDocuments/unity-sdk/#set-callback"),
    unreal: DocsUtil.makeDocsUrl("/sdkDocuments/unreal-sdk/#set-callback"),
  }
}, {
  value: 'valueMapOthers.txt',  // userKV
  alias: 'CustomizedData.txt',
  label: ze('自定义KV', 'Custom Key-Values'),
  dataSourceType: 'API',
  dataSourceDesc: 'AddSceneData / SetUserValue',
  tagMobileMax: '128KB',
  tagPcMax: '128KB',
  noteMobileMax:ze('KV长度之和不超过128KB，KV数不超过10000对，单个KV长度不超过100。','The sum of KV lengths should not exceed 128KB, the number of KVs should not exceed 10,000, and the length of the single KV should not exceed 100.'),
  notePcMax:ze('KV长度之和小于128KB。','The sum of KV lengths should should not exceed 128KB.'),
  link: {
    android: DocsUtil.makeDocsUrl('/sdkDocuments/mobile-sdk/#add-custom-data'),
    ios: DocsUtil.makeDocsUrl('/sdkDocuments/mobile-sdk/#set-custom-key-value-data'),
    pc: DocsUtil.makeDocsUrl("/sdkDocuments/pc-sdk/#custom-kv"),
    unity: DocsUtil.makeDocsUrl("/sdkDocuments/unity-sdk/#add-custom-data"),
    unreal: DocsUtil.makeDocsUrl("/sdkDocuments/unreal-sdk/#add-custom-data"),
  },
}, {
  value: 'anrMessage.txt',
  alias: 'AnrMessage.txt',
  dataSourceDesc: ze('SDK采集的anr信息',' Anr message captured by SDK'),
}, {
  ...CUSTOM_LOG_COMMON_FILE_INFO,
  value: 'crashSightLog.log', // PC的自定义日志
  isBase64Encoded: true,
}, {
  ...CUSTOM_LOG_COMMON_FILE_INFO,
  value: 'crashsightlog.zip', // 安卓的自定义日志
  isBase64Encoded: false,
}, {
  ...CUSTOM_LOG_COMMON_FILE_INFO,
  value: 'crashsightlog.txt', // iOS的自定义日志
  isBase64Encoded: false,
}, {
  value: 'logFile.log', // PC的附件，最早的SDK后台上报版本。新版本SDK改成了SDK直传方式（CustomizedLogFile.zip和CustomizedLogFile.gz）
  alias: 'CustomizedLogFile.log',
  isBase64Encoded: true,
  label: i18n.t('REPORTDETAIL.自定义日志来自文件'),
  dataSourceType: 'API',
  dataSourceDesc: 'SetCustomLogDir',
  noteMobileMax:ze('压缩前不超过20MB，压缩后不超过10MB（上传过程中会对数据进行压缩），可通过云控和接口更改日志附件大小。',' The uncompressed size should not exceed 20MB, and the compressed size should not exceed 10MB (data will be compressed during the upload process). The size of log attachments can be changed through strategy and interface.'),
  notePcMax:ze('压缩前不超过5MB。','The uncompressed size should not exceed 5MB.'),
  link: {
    pc: DocsUtil.makeDocsUrl("/sdkDocuments/pc-sdk/#specify-the-log-directory"),
    unity: DocsUtil.makeDocsUrl("/sdkDocuments/unity-sdk/#set-log-uploading-callback"),
    unreal: DocsUtil.makeDocsUrl("/sdkDocuments/unreal-sdk/#set-log-uploading-callback"),
  }
}, {
  value: 'custom_attachment.bin', // PS5用的附件文件
  alias: 'CustomAttachment.bin',
  dataSourceType: 'API',
  dataSourceDesc: 'SetLogPath',
  noteMax: ze('附件大小不超过1MB','The attachment file size should not exceed 1MB.'),
  link: {
    unity: DocsUtil.makeDocsUrl("/sdkDocuments/unity-sdk-new/#set-upload-log-path"),
    unreal: DocsUtil.makeDocsUrl("/sdkDocuments/unreal-sdk-new/#set-upload-log-path"),
  }
}, {
  value: 'stackMemoryInfo.txt', // iOS栈信息
  alias: 'StackMemoryInfo.txt',
  dataSourceDesc: ze('SDK自动采集上报栈内存信息', 'The SDK automatically collects and reports stack memory information')
}, {
  value: MOBILE_SDK_LOG_FILENAME, // 这个是SDK直接上传的log,mobile附件
  dataSourceType: 'API',
  dataSourceDesc: 'SetCrashLogObserver',
  tagMobileMax: '20MB',
  tagPcMax: '5MB',
  tagMobileMaxAfter: '10MB',
  noteMobileMax: ze('压缩前不超过20MB，压缩后不超过10MB（上传过程中会对数据进行压缩），可通过云控更改日志附件大小。',' The uncompressed size should not exceed 20MB, and the compressed size should not exceed 10MB (data will be compressed during the upload process). The size of log attachments can be changed through server side config.'),
  notePcMax: ze('压缩前不超过20MB，压缩后不超过10MB','The uncompressed size should not exceed 20MB, and the compressed size should not exceed 10MB.'),
  link: {
    unity: DocsUtil.makeDocsUrl("/sdkDocuments/unreal-sdk-new/#set-log-upload-callback"),
    unreal: DocsUtil.makeDocsUrl("/sdkDocuments/unity-sdk-new/#set-log-uploading-callback"),
  },
}, {
  value: PC_SDK_LOG_FILENAME, // SDK直接上传的log,pc附件
  isBase64Encoded: true,
  label: i18n.t('REPORTDETAIL.自定义日志来自文件'),
  dataSourceType: 'API',
  dataSourceDesc: 'SetCustomLogDir',
  noteMobileMax: ze('压缩前不超过20MB，压缩后不超过10MB（上传过程中会对数据进行压缩），可通过云控和接口更改日志附件大小。',' The uncompressed size should not exceed 20MB, and the compressed size should not exceed 10MB (data will be compressed during the upload process). The size of log attachments can be changed through strategy and interface.'),
  notePcMax: ze('压缩前不超过20MB，压缩后不超过10MB','The uncompressed size should not exceed 20MB, and the compressed size should not exceed 10MB.'),
  link: {
    pc: DocsUtil.makeDocsUrl("/sdkDocuments/pc-sdk/#specify-the-log-directory"),
    unity: DocsUtil.makeDocsUrl("/sdkDocuments/unity-sdk/#set-log-uploading-callback"),
    unreal: DocsUtil.makeDocsUrl("/sdkDocuments/unreal-sdk/#set-log-uploading-callback"),
  }
}, {
  value: PC_SDK_LOG_FILENAME_NEW_FORMAT, // SDK直接上传的log,pc附件（新的zip格式）
  isBase64Encoded: true,
  label: i18n.t('REPORTDETAIL.自定义日志来自文件'),
  dataSourceType: 'API',
  dataSourceDesc: 'SetCustomLogDir',
  noteMobileMax: ze('压缩前不超过20MB，压缩后不超过10MB（上传过程中会对数据进行压缩），可通过云控和接口更改日志附件大小。',' The uncompressed size should not exceed 20MB, and the compressed size should not exceed 10MB (data will be compressed during the upload process). The size of log attachments can be changed through strategy and interface.'),
  notePcMax: ze('压缩前不超过20MB，压缩后不超过10MB','The uncompressed size should not exceed 20MB, and the compressed size should not exceed 10MB.'),
  link: {
    pc: DocsUtil.makeDocsUrl("/sdkDocuments/pc-sdk/#specify-the-log-directory"),
    unity: DocsUtil.makeDocsUrl("/sdkDocuments/unity-sdk/#set-custom-log-path"),
    unreal: DocsUtil.makeDocsUrl("/sdkDocuments/unreal-sdk/#set-custom-log-path"),
  },
}, {
  value: SDK_ATTACK_FILENAME, // SDK新增的直传cos的其他类附件（和log类附件区分）
  isBase64Encoded: true,
  dataSourceType: 'API',
  dataSourceDesc: 'SetCustomAttachDir',
  noteMobileMax: ze('压缩前不超过20MB，压缩后不超过10MB（上传过程中会对数据进行压缩），可通过云控和接口更改日志附件大小。',' The uncompressed size should not exceed 20MB, and the compressed size should not exceed 10MB (data will be compressed during the upload process). The size of log attachments can be changed through strategy and interface.'),
  notePcMax: ze('压缩前不超过20MB，压缩后不超过10MB','The uncompressed size should not exceed 20MB, and the compressed size should not exceed 10MB.'),
  link: {
    pc: DocsUtil.makeDocsUrl("/sdkDocuments/pc-sdk/#specify-the-attachment-directory"),
    unity: DocsUtil.makeDocsUrl("/sdkDocuments/unity-sdk/#set-custom-attachment-path"),
    unreal: DocsUtil.makeDocsUrl("/sdkDocuments/unreal-sdk/#set-custom-attachment-path"),
  },
}, {
  value: MINIDUMP_STACK_MEMORY_FILENAME,
  alias: MINIDUMP_STACK_MEMORY_FILENAME,
  dataSourceDesc: ze('Minidump栈内存信息', 'Minidump Stack Memory Info'),
}, {
  value: 'Minidump.dmp.gz', // minidump文件
  alias: 'Minidump.dmp.gz',
  isBase64Encoded: true,
  dataSourceDesc: ze('崩溃时自动采集的minidump文件', 'The SDK automatically collects and reports Minidump file on crash'),
}, {
  value: 'PsAttachments.zip', // ps5的附件
  alias: 'PsAttachments.zip',
  dataSourceDesc: ze('SIE Recap Server', 'SIE Recap Server'),
}, {
  value: PLAYSTATION_CORE_DUMP_FILE_SET_FILENAME,
  alias: 'Core Dump File Set',
  dataSourceDesc: ze('系统自动生成的附件集合', 'Files generated by the system.'),
}, {
  value: 'tty.log',
  alias: 'TTY.log',
  dataSourceDesc: ze('SIE Recap Server', 'SIE Recap Server'),
}, {
  value: 'aexit.zip',
  alias: 'Aexit.zip',
  isBase64Encoded: true,
  dataSourceDesc: ze('Android ApplicationExitInfo 原始退出信息', 'Raw Android ApplicationExitInfo'),
}, {
  value: 'kernel_tty.log',
  alias: 'KernelTTY.log',
  dataSourceDesc: ze('SIE Recap Server', 'SIE Recap Server'),
}];

export const getHelpLink = (subAppType,pid,fileName) => {
  let helpLink = null;
  const fileInfo = CrashAttachmentUtil.getInfoByFilename(fileName);
  if(fileInfo && fileInfo.link){
    // 先根据引擎获取
    if(subAppType){
      if(subAppType === 'UNITY'){
        helpLink = fileInfo.link.unity;
      }else if(subAppType === 'UE4' || subAppType === 'UE5'){
        helpLink = fileInfo.link.unreal;
      }
    }
    // 再根据平台获取
    if(isNullish(helpLink)){
      if(isAndroidOrHarmony(pid)){
        helpLink = fileInfo.link.android;
      }else if(isIos(pid)){
        helpLink = fileInfo.link.ios;
      }else if(isPcOrLinux(pid)){
        helpLink = fileInfo.link.pc;
      }
    }
  }
  return helpLink;
}

const knownAttachmentFilenameToInfo = Object.assign({}, ...knownAttachmentFileInfoList.map(x => ({ [x.value]: x })));

/** @type {string[]} */
const knownAttachmentFilenames = knownAttachmentFileInfoList.map(x => x.value);

export const CrashAttachmentUtil = Object.freeze({
  MINIDUMP_STACK_MEMORY_FILENAME,
  PLAYSTATION_CORE_DUMP_FILE_SET_FILENAME,

  getSdkLogFilename(pid, sdkVersion) {
    if (isWechatMiniProgram(pid)) {
      return WECHAT_MINI_PROGRAM_SDK_LOG_FILENAME;
    } else if (PlatformUtil.isMobileOrMobileLike(pid)) {
      return MOBILE_SDK_LOG_FILENAME;
    } else {
      return this.checkIsZipFormatPcSdkLogFile(sdkVersion) ? PC_SDK_LOG_FILENAME_NEW_FORMAT : PC_SDK_LOG_FILENAME;
    }
  },

  getSdkAttachFilename(pid, sdkVersion) {
    return SDK_ATTACK_FILENAME;
  },

  /** 是否是新的PC客户端直传附件格式。2.2.4版本开始从.gz改成了.zip。 */
  checkIsZipFormatPcSdkLogFile(sdkVersion) {
    return VersionUtil.compareCsSdkVersion(sdkVersion, '2.2.4') >= 0;
  },

  /** 生成SDK附件下载用的crashMap, 对应后台的FetchSdkAttachmentsPartialCrashMap类 */
  makePartialCrashMapForSdkUploadedFile(crashMap) {
    const { crashId, expUid, productVersion, sdkVersion, deviceId, sdkAttachmentBucket } = crashMap || {};
    return { crashId, expUid, productVersion, sdkVersion, deviceId, sdkAttachmentBucket };
  },

  /** 对于SDK直传的文件，生成下载用的axios get参数 */
  makeRequestParamsForSdkUploadedFile(filename, appId, pid, crashMap) {
    const { expUid, productVersion: crashVersion, sdkVersion } = crashMap || {};
    if (!expUid || !crashVersion) {
      console.warn('asyncMakeRequestParamsForSdkUploadedFile, no expUid or crashVersion provided!');
      return null;
    }

    let isAttachNotLog;
    if (this.getSdkLogFilename(pid, sdkVersion) === filename) {
      isAttachNotLog = false;
    } else if (this.getSdkAttachFilename(pid, sdkVersion) === filename) {
      isAttachNotLog = true;
    } else {
      throw Error(`asyncMakeRequestParamsForSdkUploadedFile, invalid filename = ${filename}!`);
    }

    const expUidWithoutHyphenLowerCase = expUid.replace(/-/g, '').toLowerCase();
    const expUidPrefix = expUidWithoutHyphenLowerCase.slice(0, 16);

    // TODO：Android SDK 4.2.9版本的MD5 Hex计算有bug，Hex的前导0被全部丢掉了
    const versionMd5Prefix = (sdkVersion || '').startsWith('4.2.9') && isAndroidOrHarmony(pid)
      ? new MD5().update(crashVersion).digest('hex').replace(/^0+/g, '').slice(0, 8)
      : new MD5().update(crashVersion).digest('hex').slice(0, 8);

    const encryptKey = expUidWithoutHyphenLowerCase;
    const encryptKeyMd5Hex = new MD5().update(encryptKey).digest('hex');

    function hexToBytes(hex) {
      let bytes = [];
      for (let c = 0; c < hex.length; c += 2) {
        bytes.push(parseInt(hex.substr(c, 2), 16));
      }
      return bytes;
    }
    function bytesToBase64(bytes) {
      return window.btoa(String.fromCharCode(...bytes));
    }

    const urlOrigin = getBackendInjectEnvs().cosSdkLogsDownloadOrigin;
    const isAmazonAws = urlOrigin.includes('amazonaws.com');
    const headerStorage = isAmazonAws ? 'amz' : 'cos';

    const filenameSuffix = isAttachNotLog ? '-attach' : '';

    let url;
    if (isHarmony(pid)) {
      url = `${urlOrigin}/${expUidPrefix}-${appId}-${versionMd5Prefix}${filenameSuffix}.zip`;
    } else if (PlatformUtil.isMobileOrMobileLike(pid)) {
      url = `${urlOrigin}/${expUidPrefix}/${appId}/${versionMd5Prefix}${filenameSuffix}.zip`;
    } else {
      url = CrashAttachmentUtil.checkIsZipFormatPcSdkLogFile(sdkVersion)
        ? `${urlOrigin}/${expUidPrefix}-${appId}-${versionMd5Prefix}${filenameSuffix}.zip`
        : `${urlOrigin}/${expUidPrefix}-${appId}-${versionMd5Prefix}${filenameSuffix}.gz`;
    }

    const config = {
      responseType: 'arraybuffer',
      headers: {
        [`x-${headerStorage}-server-side-encryption-customer-algorithm`]: 'AES256',
        [`x-${headerStorage}-server-side-encryption-customer-key`]: window.btoa(encryptKey),
        [`x-${headerStorage}-server-side-encryption-customer-key-MD5`]: bytesToBase64(hexToBytes(encryptKeyMd5Hex)),
      },
    };
    return [url, config];
  },

  getKnownAttachmentFileInfoList() {
    return knownAttachmentFileInfoList;
  },

  getKnownAttachmentFilenames() {
    return knownAttachmentFilenames;
  },

  getInfoByFilename(filename) {
    return knownAttachmentFilenameToInfo[filename];
  },

  getFileAliasByFilename(filename) {
    return knownAttachmentFilenameToInfo[filename]?.alias ?? filename;
  },

  refineFileContent(pid, filename, content) {
    if (filename === 'trace.zip') {
      // 和平需求，trace.zip里面的内容，线程之间都加一个空行。因为线程名是引号开头的，所以处理方式是用正则把每行开头的"替换成\n"
      return content?.replace(/^"/gm, '\n"');
    }
    if (!isMobile(pid) || filename !== 'valueMapOthers.txt') {
      return content;
    }
    if (!isString(content)) {
      return content;
    }
    // 移动端的valueMapOthers展示的时候，要把分号分割改成换行分割，然后去掉C03之类前缀
    return (content || '')
      .split(';')
      .filter(x => !isMobile(pid) || /^(C03|C06)/.test(x)) // 移动端只保留C03, C06开头的，其他全部过滤
      .map(x => x.replace(/^(C03_|C04_|C06)/, ''))
      .join('\n');
  },

  // 解析stacktrace.zip附件的内容，转换为attachList格式
  parseAndroidNativeOtherThreadContent(content) {
    /* 文件格式示例：
======= Stack Trace Report =======
Dump Time: 1744706514
Process ID: 756
Thread Count: 38
[Thread 756]
Name: ht.android.demo
Stack:
#00    pc 0000000000040574    /apex/com.android.runtime/lib64/bionic/libc.so (syscall+36) [arm64-v8a::9e95ade9454f0c544b6d804e902a6c5d]
#01    pc 0000000000090578    /data/app/~~GR4xsCYMMAE9d9iRtgiiBA==/com.tencent.crashsight.android.demo-K7B1SEcQQIxPDPAcNkDpSw==/lib/arm64/libCrashSight.so [arm64-v8a::468529619d3a89bad3ee6d8b86687b4c]
#02    pc 0000000000094948    /data/app/~~GR4xsCYMMAE9d9iRtgiiBA==/com.tencent.crashsight.android.demo-K7B1SEcQQIxPDPAcNkDpSw==/lib/arm64/libCrashSight.so [arm64-v8a::468529619d3a89bad3ee6d8b86687b4c]
......
----------------------------
[Thread 8079]
Name: Signal Catcher
Stack:
#00    pc 000000000007e96c    /apex/com.android.runtime/lib64/bionic/libc.so (freopen64+512) [arm64-v8a::9e95ade9454f0c544b6d804e902a6c5d]
#01    pc 000000000002f904    /apex/com.android.runtime/lib64/bionic/libc.so (sigwait64+92) [arm64-v8a::9e95ade9454f0c544b6d804e902a6c5d]
#02    pc 000000000067422c    /apex/com.android.art/lib64/libart.so [arm64-v8a::f09f55f58888db3fa9c586c60e5e1385]
#03    pc 000000000002a114    /apex/com.android.runtime/lib64/bionic/libc.so [arm64-v8a::9e95ade9454f0c544b6d804e902a6c5d]
#04    pc 000000000001c230    /apex/com.android.runtime/lib64/bionic/libc.so [arm64-v8a::9e95ade9454f0c544b6d804e902a6c5d]
----------------------------
======= End of Report =======
    *  */
    if (!content) {
      return [];
    }

    // 按线程分隔符分割内容
    const threadSections = content.split(/(?=\[Thread \d+\])/);
    const result = [];

    for (const section of threadSections) {
      if (!section.trim()) {
        continue;
      }

      // 提取线程ID
      const threadIdMatch = section.match(/\[Thread (\d+)\]/);
      if (!threadIdMatch) {
        continue;
      }

      const threadId = threadIdMatch[1];

      // 提取线程名称
      const nameMatch = section.match(/Name:\s*(.+)/);
      const threadName = nameMatch ? nameMatch[1] : 'Unknown';

      // 提取堆栈内容（从"Stack:"开始到下一个分隔符或文件结尾）
      const stackStartIndex = section.indexOf('Stack:');
      if (stackStartIndex === -1) {
        continue;
      }

      let stackContent = section.slice(stackStartIndex + 'Stack:'.length);
      // 移除可能的分隔线
      stackContent = stackContent.split(/-{10,}/)[0].trim();

      result.push({
        threadId,
        threadName,
        stackContent,
      });
    }

    return result.map(x => {
      const fileName = `${x.threadName}(${x.threadId})`;
      return {
        fileName,
        content: x.stackContent,
        fileType: CrashAttachmentFileType.OTHER_THREAD,
      };
    });
  },

  // 解析stacktracejson.zip附件的内容，转换为attachList格式
  parseAndroidNativeNewFormatOtherThreadContent(content) {
    if (!content) {
      return [];
    }
    const { threads } = JSON.parse(content);
    return threads.map(x => {
      const {
        name: threadName,
        stack: stack,
        tid: threadId,
        stackSize,
        stackUsed,
        stat,
        syscall,
        wchan,
      } = x;

      const fileName = `${threadName}(${threadId})`;
      return {
        fileName,
        content: stack,
        fileType: CrashAttachmentFileType.OTHER_THREAD,
        otherThreadExtraInfo: x,
      }
    });
  },
});
